import React, { useContext, useEffect, useState } from 'react';
import FormGroup from '@mui/material/FormGroup';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconsSend from '@mui/icons-material/Send';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import InputTranslationTextField from '../../../../components/_input/InputTranslation/InputTranslationTextField';
import { useTestPaypalConfigPaymentMethodMutation } from '../../../../../graphql/mutation/paymentMethod/testPaypalConfigPaymentMethodMutation';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';
import { useUpdatePaymentMethodMutation } from '../../../../../graphql/mutation/paymentMethod/updatePaymentMethodMutation';
import { useCreatePaymentMethodMutation } from '../../../../../graphql/mutation/paymentMethod/createPaymentMethodMutation';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import { usePaymentMethodQuery } from '../../../../../graphql/query/paymentMethod/usePaymentMethodQuery';
import NotFound from '../../../../components/_error/NotFound';
import useRoutes from '../../../../components/_hook/useRoutes';
import Select, { IValue } from '../../../../components/_select/_module/Select';
import InputPassword from '../../../../components/_input/InputPassword';
import SelectSearchCompany from '../../../../components/_select/SelectSearchCompany';
import SelectCountry from '../../../../components/_select/SelectCountry';
import SelectCurrency from '../../../../components/_select/SelectCurrency';
import InputPrice from '../../../../components/_input/InputPrice';
import { ITranslationSubTypeInput } from '../../../../../graphql/type/_inputTypes/translation';
import InputTranslationTextarea from '../../../../components/_input/InputTranslation/InputTranslationTextarea';
import BusinessLayout from '../../../../layout/Business';
import Context from '../../../../context';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
}));

function SuperPaymentMethodDetail() {
  interface IFormValue {
    name: ITranslationSubTypeInput[];
    description: ITranslationSubTypeInput[];
    status: boolean;
    paymentMethodType: string;
    company?: string;

    paymentChargeBy?: IValue;

    paymentApiKey?: string;
    paymentApiSecret?: string;
    paymentPayeeId?: string;

    paymentMode?: string;
    paymentClientId?: string;
    paymentClientSecret?: string;

    restrictionCountry?: string;
    restrictionCurrency?: string;
    restrictionMinAmount: string;
    restrictionMaxAmount: string;

    paymentChargeBuyerType?: string;
    paymentChargeBuyerFixed: string;
    paymentChargeBuyerPercentage: string;
    paymentChargeBuyerMinCharge: string;
    paymentChargeBuyerMaxCharge: string;
    paymentChargeSellerType?: string;
    paymentChargeSellerFixed: string;
    paymentChargeSellerPercentage: string;
    paymentChargeSellerMinCharge: string;
    paymentChargeSellerMaxCharge: string;

    keepitalPaymentChargeBuyerType?: string;
    keepitalPaymentChargeBuyerFixed: string;
    keepitalPaymentChargeBuyerPercentage: string;
    keepitalPaymentChargeBuyerMinCharge: string;
    keepitalPaymentChargeBuyerMaxCharge: string;
    keepitalPaymentChargeSellerType?: string;
    keepitalPaymentChargeSellerFixed: string;
    keepitalPaymentChargeSellerPercentage: string;
    keepitalPaymentChargeSellerMinCharge: string;
    keepitalPaymentChargeSellerMaxCharge: string;
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { company, isSuperAdmin } = useContext(Context);
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { docID } = params;
  const paymentMethodId = docID !== 'new' ? docID : null;
  const { getText } = useLanguage();
  const [testPaypalStatus, setTestPaypalStatus] = useState<boolean | null>(
    null
  );
  const { data, loading } = usePaymentMethodQuery(
    `
			_id
			name {
				text
				la
			}
			description {
				text
				la
			}
			company
			status
			payment_method
			payment_method_config{
                charge_by
                chargeByData {
                  _id
                  name {
                      text
                      la
                  }
                }
                restrictions{
                    country
                    currency
                    min_amount
                    max_amount
                }
                payment_charges{
                    buyer{
                        fixed
                        type
                        percentage
                        min_charge
                        max_charge
                    }
                    seller{
                        fixed
                        type
                        percentage
                        min_charge
                        max_charge
                    }
                }
                keepital_payment_charges{
                    buyer{
                        fixed
                        type
                        percentage
                        min_charge
                        max_charge
                    }
                    seller{
                        fixed
                        type
                        percentage
                        min_charge
                        max_charge
                    }
                }
                api_key
                api_secret
                payee_id
                mode
                client_id
                client_secret
            }
		`,
    {
      variables: {
        filter: {
          companyId: company._id,
          _id: paymentMethodId as string,
        },
      },
      skip: !paymentMethodId,
    }
  );

  function onCompleted() {
    notification.success(t('success'));
    history.push(
      path('business.payment_method.list', {
        companyID: company._id,
      })
    );
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    createPaymentMethodMutation,
    { loading: isCreatingPaymentMethod },
  ] = useCreatePaymentMethodMutation(
    `createdPaymentMethod {
				_id
			}`,
    {
      onCompleted,
      onError,
    }
  );
  const [
    updatePaymentMethodMutation,
    { loading: isUpdatingPaymentMethod },
  ] = useUpdatePaymentMethodMutation(
    `updatedPaymentMethod {
				_id
                name {
                    text
                    la
                }
                description {
                    text
                    la
                }
                company
                status
                payment_method
                payment_method_config{
                    charge_by
                    chargeByData {
                      _id
                      name {
                          text
                          la
                      }
                    }
                    restrictions{
                        country
                        currency
                        min_amount
                        max_amount
                    }
                    payment_charges{
                        buyer{
                            fixed
                            type
                            percentage
                            min_charge
                            max_charge
                        }
                        seller{
                            fixed
                            type
                            percentage
                            min_charge
                            max_charge
                        }
                    }
                    keepital_payment_charges {
                        buyer{
                            fixed
                            type
                            percentage
                            min_charge
                            max_charge
                        }
                        seller{
                            fixed
                            type
                            percentage
                            min_charge
                            max_charge
                        }
                    }
                    api_key
                    api_secret
                    payee_id
                    mode
                    client_id
                    client_secret
				}
				
				updated {
					time
				}
			}`,
    {
      onCompleted,
      onError,
    }
  );
  const [
    testPaypalConfigPaymentMethodMutation,
    { loading: isTestingPaypalConfigPaymentMethod },
  ] = useTestPaypalConfigPaymentMethodMutation<{ isSuccess: boolean }>(
    'isSuccess',
    {
      onCompleted: (tempData) => {
        if (tempData && tempData.testPaypalConfigPaymentMethod) {
          setTestPaypalStatus(tempData.testPaypalConfigPaymentMethod.isSuccess);
        } else {
          setTestPaypalStatus(false);
        }
      },
      onError: (e) => notification.error(e.message),
    }
  );

  async function onSubmitForm(values: IFormValue) {
    const tempValues = {
      name: values.name,
      description: values.description,
      status: values.status,
      paymentMethodType: values.paymentMethodType,
      company: values.company,
      paymentChargeBy: values.paymentChargeBy
        ? values.paymentChargeBy.value
        : undefined,
      paymentApiKey: values.paymentApiKey,
      paymentApiSecret: values.paymentApiSecret,
      paymentPayeeId: values.paymentPayeeId,
      paymentMode: values.paymentMode,
      paymentClientId: values.paymentClientId,
      paymentClientSecret: values.paymentClientSecret,
      restrictionCountry: values.restrictionCountry,
      restrictionCurrency: values.restrictionCurrency,
      restrictionMinAmount: parseFloat(values.restrictionMinAmount),
      restrictionMaxAmount: parseFloat(values.restrictionMaxAmount),
      paymentChargeBuyerType: values.paymentChargeBuyerType,
      paymentChargeBuyerFixed: parseFloat(values.paymentChargeBuyerFixed),
      paymentChargeBuyerPercentage: parseFloat(
        values.paymentChargeBuyerPercentage
      ),
      paymentChargeBuyerMinCharge: parseFloat(
        values.paymentChargeBuyerMinCharge
      ),
      paymentChargeBuyerMaxCharge: parseFloat(
        values.paymentChargeBuyerMaxCharge
      ),
      paymentChargeSellerType: values.paymentChargeSellerType,
      paymentChargeSellerFixed: parseFloat(values.paymentChargeSellerFixed),
      paymentChargeSellerPercentage: parseFloat(
        values.paymentChargeSellerPercentage
      ),
      paymentChargeSellerMinCharge: parseFloat(
        values.paymentChargeSellerMinCharge
      ),
      paymentChargeSellerMaxCharge: parseFloat(
        values.paymentChargeSellerMaxCharge
      ),
      keepitalPaymentChargeBuyerType: values.keepitalPaymentChargeBuyerType,
      keepitalPaymentChargeBuyerFixed: parseFloat(
        values.keepitalPaymentChargeBuyerFixed
      ),
      keepitalPaymentChargeBuyerPercentage: parseFloat(
        values.keepitalPaymentChargeBuyerPercentage
      ),
      keepitalPaymentChargeBuyerMinCharge: parseFloat(
        values.keepitalPaymentChargeBuyerMinCharge
      ),
      keepitalPaymentChargeBuyerMaxCharge: parseFloat(
        values.keepitalPaymentChargeBuyerMaxCharge
      ),
      keepitalPaymentChargeSellerType: values.keepitalPaymentChargeSellerType,
      keepitalPaymentChargeSellerFixed: parseFloat(
        values.keepitalPaymentChargeSellerFixed
      ),
      keepitalPaymentChargeSellerPercentage: parseFloat(
        values.keepitalPaymentChargeSellerPercentage
      ),
      keepitalPaymentChargeSellerMinCharge: parseFloat(
        values.keepitalPaymentChargeSellerMinCharge
      ),
      keepitalPaymentChargeSellerMaxCharge: parseFloat(
        values.keepitalPaymentChargeSellerMaxCharge
      ),
    };
    if (data) {
      await updatePaymentMethodMutation({
        variables: {
          _id: data.paymentMethods.items[0]._id,
          ...tempValues,
        },
      });
    } else {
      await createPaymentMethodMutation({ variables: tempValues });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      name: [],
      description: [],
      status: true,
      paymentMethodType: 'standard_payment',
      company: company._id,
      paymentChargeBy: {
        label: getText(company.name),
        value: company._id,
      },

      paymentApiKey: '',
      paymentApiSecret: '',
      paymentPayeeId: '',

      paymentMode: '',
      paymentClientId: '',
      paymentClientSecret: '',

      restrictionCountry: '',
      restrictionCurrency: '',
      restrictionMinAmount: '',
      restrictionMaxAmount: '',

      paymentChargeBuyerType: '',
      paymentChargeBuyerFixed: '',
      paymentChargeBuyerPercentage: '',
      paymentChargeBuyerMinCharge: '',
      paymentChargeBuyerMaxCharge: '',
      paymentChargeSellerType: '',
      paymentChargeSellerFixed: '',
      paymentChargeSellerPercentage: '',
      paymentChargeSellerMinCharge: '',
      paymentChargeSellerMaxCharge: '',

      keepitalPaymentChargeBuyerType: '',
      keepitalPaymentChargeBuyerFixed: '',
      keepitalPaymentChargeBuyerPercentage: '',
      keepitalPaymentChargeBuyerMinCharge: '',
      keepitalPaymentChargeBuyerMaxCharge: '',
      keepitalPaymentChargeSellerType: '',
      keepitalPaymentChargeSellerFixed: '',
      keepitalPaymentChargeSellerPercentage: '',
      keepitalPaymentChargeSellerMinCharge: '',
      keepitalPaymentChargeSellerMaxCharge: '',
    },
    onSubmit: onSubmitForm,
    onValidate: (tempValues) => {
      const newErrors: Record<string, string> = {};

      if (!tempValues.name.length || !getText(tempValues.name)) {
        newErrors.name = t('please_enter_the_correct__type__', {
          type: t('name'),
        });
      }

      if (!tempValues.paymentMethodType) {
        newErrors.paymentMethodType = t('please_enter_the_correct__type__', {
          type: t('payment_method'),
        });
      }

      if (tempValues.paymentMethodType === 'paypal') {
        if (!tempValues.paymentMode) {
          newErrors.paymentMode = t('__type__is_required', { type: t('mode') });
        }
      }

      Object.keys(newErrors).forEach((newError) =>
        notification.error(newErrors[newError])
      );

      return newErrors;
    },
  });

  useEffect(() => {
    if (!loading && data && data.paymentMethods.items.length) {
      const paymentMethod = data.paymentMethods.items[0];
      const paymentMethodConfig = paymentMethod.payment_method_config;
      const { restrictions } = paymentMethodConfig;
      const paymentCharges = paymentMethodConfig.payment_charges;
      const keepitalPaymentCharges =
        paymentMethodConfig.keepital_payment_charges;

      setValues({
        name: paymentMethod.name.map((name) => ({
          text: name.text,
          la: name.la,
        })),
        description: paymentMethod.description.map((description) => ({
          text: description.text,
          la: description.la,
        })),
        status: paymentMethod.status,
        paymentMethodType: paymentMethod.payment_method,
        company: paymentMethod.company,
        paymentChargeBy: paymentMethodConfig.chargeByData
          ? {
              label: getText(paymentMethodConfig.chargeByData.name),
              value: paymentMethodConfig.chargeByData._id,
            }
          : undefined,
        paymentApiKey: paymentMethodConfig.api_key,
        paymentApiSecret: paymentMethodConfig.api_secret,
        paymentPayeeId: paymentMethodConfig.payee_id,
        paymentMode: paymentMethodConfig.mode,
        paymentClientId: paymentMethodConfig.client_id,
        paymentClientSecret: paymentMethodConfig.client_secret,
        restrictionCountry: restrictions ? restrictions.country : '',
        restrictionCurrency: restrictions ? restrictions.currency : '',
        restrictionMinAmount:
          restrictions && restrictions.min_amount
            ? restrictions.min_amount.toString()
            : '',
        restrictionMaxAmount:
          restrictions && restrictions.max_amount
            ? restrictions.max_amount.toString()
            : '',
        paymentChargeBuyerType:
          paymentCharges && paymentCharges.buyer
            ? paymentCharges.buyer.type
            : '',
        paymentChargeBuyerFixed:
          paymentCharges && paymentCharges.buyer && paymentCharges.buyer.fixed
            ? paymentCharges.buyer.fixed.toString()
            : '',
        paymentChargeBuyerPercentage:
          paymentCharges &&
          paymentCharges.buyer &&
          paymentCharges.buyer.percentage
            ? paymentCharges.buyer.percentage.toString()
            : '',
        paymentChargeBuyerMinCharge:
          paymentCharges &&
          paymentCharges.buyer &&
          paymentCharges.buyer.min_charge
            ? paymentCharges.buyer.min_charge.toString()
            : '',
        paymentChargeBuyerMaxCharge:
          paymentCharges &&
          paymentCharges.buyer &&
          paymentCharges.buyer.max_charge
            ? paymentCharges.buyer.max_charge.toString()
            : '',
        paymentChargeSellerType:
          paymentCharges && paymentCharges.seller
            ? paymentCharges.seller.type
            : '',
        paymentChargeSellerFixed:
          paymentCharges && paymentCharges.seller && paymentCharges.seller.fixed
            ? paymentCharges.seller.fixed.toString()
            : '',
        paymentChargeSellerPercentage:
          paymentCharges &&
          paymentCharges.seller &&
          paymentCharges.seller.percentage
            ? paymentCharges.seller.percentage.toString()
            : '',
        paymentChargeSellerMinCharge:
          paymentCharges &&
          paymentCharges.seller &&
          paymentCharges.seller.min_charge
            ? paymentCharges.seller.min_charge.toString()
            : '',
        paymentChargeSellerMaxCharge:
          paymentCharges &&
          paymentCharges.seller &&
          paymentCharges.seller.max_charge
            ? paymentCharges.seller.max_charge.toString()
            : '',
        keepitalPaymentChargeBuyerType:
          keepitalPaymentCharges && keepitalPaymentCharges.buyer
            ? keepitalPaymentCharges.buyer.type
            : '',
        keepitalPaymentChargeBuyerFixed:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.buyer &&
          keepitalPaymentCharges.buyer.fixed
            ? keepitalPaymentCharges.buyer.fixed.toString()
            : '',
        keepitalPaymentChargeBuyerPercentage:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.buyer &&
          keepitalPaymentCharges.buyer.percentage
            ? keepitalPaymentCharges.buyer.percentage.toString()
            : '',
        keepitalPaymentChargeBuyerMinCharge:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.buyer &&
          keepitalPaymentCharges.buyer.min_charge
            ? keepitalPaymentCharges.buyer.min_charge.toString()
            : '',
        keepitalPaymentChargeBuyerMaxCharge:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.buyer &&
          keepitalPaymentCharges.buyer.max_charge
            ? keepitalPaymentCharges.buyer.max_charge.toString()
            : '',
        keepitalPaymentChargeSellerType:
          keepitalPaymentCharges && keepitalPaymentCharges.seller
            ? keepitalPaymentCharges.seller.type
            : '',
        keepitalPaymentChargeSellerFixed:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.seller &&
          keepitalPaymentCharges.seller.fixed
            ? keepitalPaymentCharges.seller.fixed.toString()
            : '',
        keepitalPaymentChargeSellerPercentage:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.seller &&
          keepitalPaymentCharges.seller.percentage
            ? keepitalPaymentCharges.seller.percentage.toString()
            : '',
        keepitalPaymentChargeSellerMinCharge:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.seller &&
          keepitalPaymentCharges.seller.min_charge
            ? keepitalPaymentCharges.seller.min_charge.toString()
            : '',
        keepitalPaymentChargeSellerMaxCharge:
          keepitalPaymentCharges &&
          keepitalPaymentCharges.seller &&
          keepitalPaymentCharges.seller.max_charge
            ? keepitalPaymentCharges.seller.max_charge.toString()
            : '',
      });
    }
  }, [loading]);

  const paymentChargesTypeOptions = [
    {
      label: t('none'),
      value: '',
    },
    {
      label: t('percentage'),
      value: 'percentage',
    },
    {
      label: `${t('fixed')} ${t('amount')}`,
      value: 'fixed',
    },
    {
      label: `${t('percentage')} AND ${t('fixed')}`,
      value: 'and',
    },
    {
      label: `${t('percentage')} OR ${t('fixed')}, whichever higher`,
      value: 'or',
    },
  ];

  if (loading && paymentMethodId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (paymentMethodId && (!data || data.paymentMethods.items.length === 0)) {
    return <NotFound />;
  }

  const paymentMethod =
    paymentMethodId && data ? data.paymentMethods.items[0] : null;

  return (
    <Paper square className={classes.paper}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {paymentMethod ? (
              <>
                {t('update')} {getText(paymentMethod.name)}{' '}
                {t('payment_method')}
              </>
            ) : (
              <>
                {t('create')} {t('payment_method')}
              </>
            )}
          </Typography>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item container spacing={2}>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.status}
                      onChange={() => setValue('status', !values.status)}
                      color="primary"
                    />
                  }
                  label={t('publish')}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <InputTranslationTextField
                fullWidth
                required
                label={t('name')}
                values={values.name}
                onChange={(tempValues) => setValue('name', tempValues)}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <InputTranslationTextarea
                variant="outlined"
                fullWidth
                required
                label={t('description')}
                values={values.description}
                onChange={(tempValues) => setValue('description', tempValues)}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12}>
              <Select<IValue>
                options={[
                  ...[
                    {
                      label: t('standard_payment'),
                      value: 'standard_payment',
                    },
                    {
                      label: t('paypal'),
                      value: 'paypal',
                    },
                  ],
                  ...(isSuperAdmin
                    ? [
                        {
                          label: t('liquid_pay'),
                          value: 'liquid_pay',
                        },
                      ]
                    : []),
                ]}
                label={t('payment_method')}
                required
                menuPosition="fixed"
                fullWidth
                value={values.paymentMethodType}
                onChange={(option) =>
                  setValue('paymentMethodType', option ? option.value : '')
                }
                error={Boolean(errors.paymentMethodType)}
                helperText={errors.paymentMethodType}
              />
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item xs={12}>
              {values.paymentMethodType === 'paypal' && (
                <Card className={classes.contentCard}>
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          {t('paypal')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Select<IValue>
                          options={[
                            {
                              label: t('please_select'),
                              value: '',
                            },
                            {
                              label: 'Sandbox',
                              value: 'sandbox',
                            },
                            {
                              label: 'Live',
                              value: 'live',
                            },
                          ]}
                          label={t('mode')}
                          required
                          fullWidth
                          menuPosition="fixed"
                          value={values.paymentMode}
                          onChange={(option) =>
                            setValue('paymentMode', option ? option.value : '')
                          }
                          error={Boolean(errors.paymentMode)}
                          helperText={errors.paymentMode}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="paymentClientId"
                          label={t('client_id')}
                          value={values.paymentClientId}
                          onChange={(e) =>
                            setValue('paymentClientId', e.target.value)
                          }
                          error={Boolean(errors.paymentClientId)}
                          helperText={errors.paymentClientId}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <InputPassword
                          fullWidth
                          required
                          name="paymentClientSecret"
                          label={t('client_secret')}
                          value={values.paymentClientSecret}
                          onChange={(e) =>
                            setValue('paymentClientSecret', e.target.value)
                          }
                          error={Boolean(errors.paymentClientSecret)}
                          helperText={errors.paymentClientSecret}
                        />
                      </Grid>
                      <Grid container item xs={12} spacing={1}>
                        <Grid item xs={12}>
                          <ButtonSubmit
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              if (!values.paymentClientId) {
                                notification.error(
                                  t('__type__is_required', {
                                    type: t('client_id'),
                                  })
                                );
                              } else if (!values.paymentClientSecret) {
                                notification.error(
                                  t('__type__is_required', {
                                    type: t('client_secret'),
                                  })
                                );
                              } else if (!values.paymentMode) {
                                notification.error(
                                  t('__type__is_required', { type: t('mode') })
                                );
                              } else {
                                testPaypalConfigPaymentMethodMutation({
                                  variables: {
                                    paymentClientId: values.paymentClientId,
                                    paymentClientSecret:
                                      values.paymentClientSecret,
                                    paymentMode: values.paymentMode,
                                  },
                                });
                              }
                            }}
                            fullWidth
                            isSubmitting={isTestingPaypalConfigPaymentMethod}
                            label={t('test_config')}
                            icon={<IconsSend />}
                          />
                        </Grid>
                        {testPaypalStatus !== null && (
                          <Grid item xs={12}>
                            <Typography variant="body2">
                              {t('test_config')}:{' '}
                              {testPaypalStatus ? t('success') : t('error')}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
              {values.paymentMethodType === 'liquid_pay' && (
                <Card className={classes.contentCard}>
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          {t('liquid_pay')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Select<IValue>
                          options={[
                            {
                              label: t('please_select'),
                              value: '',
                            },
                            {
                              label: 'Sandbox',
                              value: 'sandbox',
                            },
                            {
                              label: 'Live',
                              value: 'live',
                            },
                          ]}
                          label={t('mode')}
                          required
                          fullWidth
                          menuPosition="fixed"
                          value={values.paymentMode}
                          onChange={(option) =>
                            setValue('paymentMode', option ? option.value : '')
                          }
                          error={Boolean(errors.paymentMode)}
                          helperText={errors.paymentMode}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="paymentApiKey"
                          label={t('api_key')}
                          value={values.paymentApiKey}
                          onChange={(e) =>
                            setValue('paymentApiKey', e.target.value)
                          }
                          error={Boolean(errors.paymentApiKey)}
                          helperText={errors.paymentApiKey}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <InputPassword
                          fullWidth
                          required
                          name="paymentApiSecret"
                          label={t('api_secret')}
                          value={values.paymentApiSecret}
                          onChange={(e) =>
                            setValue('paymentApiSecret', e.target.value)
                          }
                          error={Boolean(errors.paymentApiSecret)}
                          helperText={errors.paymentApiSecret}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="paymentPayeeId"
                          label={t('payee_id')}
                          value={values.paymentPayeeId}
                          onChange={(e) =>
                            setValue('paymentPayeeId', e.target.value)
                          }
                          error={Boolean(errors.paymentPayeeId)}
                          helperText={errors.paymentPayeeId}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
            <Grid item xs={12}>
              <Card className={classes.contentCard}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        {t('general')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <SelectSearchCompany<IValue>
                        label={t('charge_by')}
                        fullWidth
                        menuPosition="fixed"
                        disabled
                        placeholder={t('please_select')}
                        value={values.paymentChargeBy}
                        onChange={(option) =>
                          setValue('paymentChargeBy', option)
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card className={classes.contentCard}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        {t('restrictions')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectCountry<IValue>
                        menuPosition="fixed"
                        label={t('country')}
                        fullWidth
                        placeholder={t('please_select')}
                        value={values.restrictionCountry}
                        queryVariables={{
                          filter: {},
                        }}
                        onChange={(option) =>
                          setValue(
                            'restrictionCountry',
                            option ? option.value : ''
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectCurrency<IValue>
                        menuPosition="fixed"
                        label={t('currency')}
                        fullWidth
                        placeholder={t('please_select')}
                        value={values.restrictionCurrency}
                        onChange={(option) =>
                          setValue(
                            'restrictionCurrency',
                            option ? option.value : ''
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputPrice
                        label={t('minimum_amount')}
                        placeholder="0"
                        fullWidth
                        value={values.restrictionMinAmount}
                        currency={values.restrictionCurrency}
                        onChange={(e) =>
                          setValue('restrictionMinAmount', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InputPrice
                        label={t('maximum_amount')}
                        placeholder="0"
                        fullWidth
                        value={values.restrictionMaxAmount}
                        currency={values.restrictionCurrency}
                        onChange={(e) =>
                          setValue('restrictionMaxAmount', e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button
                variant="text"
                onClick={() =>
                  history.push(path('business.payment_method.list'))
                }
              >
                {t('cancel')}
              </Button>
            </Grid>
            <Grid item>
              <ButtonSubmit
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                isSubmitting={
                  isUpdatingPaymentMethod || isCreatingPaymentMethod
                }
                label={t('submit')}
                icon={<IconsSend />}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default () => <BusinessLayout main={<SuperPaymentMethodDetail />} />;
